import { apiConfig } from 'core-lib/config/config';
import { Environments } from 'core-lib/config/Env';
import { TRACK_EVENT } from 'core-lib/constants/nativeBridge';
import { LocalStorage, SessionStorage } from 'core-lib/constants/storage';
import { postMethod } from 'core-lib/utils/apiV2';
import { isDataValid, isObject, removeBadKeysFromObject } from 'core-lib/utils/helpers';
import { passMessageToNative } from 'core-lib/utils/nativeBridge';
import { EventTargets } from '../constants/events';
import {
  getDeviceId,
  getInitialRoute,
  getPrimarySource,
  getRefererSource,
  isParkPlusWebView,
} from './source';
import { generateBrowserFingerprint } from './browserFingerprint';
import { getDefaultHeaders } from './network';
import {
  getDefaultAttributes,
  normalizeTrackingAttributeAliases,
} from './events/attributes';
import { generateRequestId, generateShaReqId, isServer } from './utils';

const STORAGE_KEY = SessionStorage.MoengageQueuedEvents;
const EMS_EVENTS_KEY = SessionStorage.EmsQueuedEvents;
const EMS_MAX_RETRIES = 2;
const EMS_MAX_QUEUE_SIZE = 200;

function getStoredEvents(type = ''): Record<string, any> {
  try {
    const raw = sessionStorage.getItem(type === 'ems' ? EMS_EVENTS_KEY : STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    console.warn(`Invalid ${type === 'ems' ? 'EMS' : 'Moengage'} queued events JSON, resetting.`);
    return {};
  }
}

function setStoredEvents(events: Record<string, any>, type: string) {
  try {
    const keys = Object.keys(events);

    if (type === 'ems' && keys.length > EMS_MAX_QUEUE_SIZE) {
      keys.slice(0, keys.length - EMS_MAX_QUEUE_SIZE).forEach(k => delete events[k]);
    }

    sessionStorage.setItem(type === 'ems' ? EMS_EVENTS_KEY : STORAGE_KEY, JSON.stringify(events));
  } catch (err) {
    console.error(`Failed to persist ${type === 'ems' ? 'EMS' : 'Moengage'} events:`, err);
  }
}

function addEventsToSessionStorage(event: Record<string, any>, reqKey: string, type = '') {
  const events = getStoredEvents(type);
  if (reqKey in events) {
    return;
  }
  events[reqKey] =
    type === 'ems'
      ? {
          payload: event,
          retries: 0,
          createdAt: Date.now(),
        }
      : event;
  setStoredEvents(events, type);
}

function cleanupQueuedEvent(reqKey: string, type = '') {
  const events = getStoredEvents(type);
  if (events[reqKey]) {
    delete events[reqKey];
    setStoredEvents(events, type);
  }
}

function trackMoengageEvent(attributes: Record<string, any>, rKey = null) {
  const reqKey = rKey || generateRequestId();
  const { event: eventName, ...eventData } = attributes;

  // Always queue first (safe for retries)
  addEventsToSessionStorage(attributes, reqKey);

  //@ts-ignore
  if (!isServer() && window?.Moengage?.initialised) {
    try {
      //@ts-ignore
      const result = window.Moengage.track_event(eventName, eventData);

      if (result?.then) {
        result
          .then(() => cleanupQueuedEvent(reqKey))
          .catch((err: any) => {
            console.error('Moengage event tracking failed:', err);
          });
      } else {
        cleanupQueuedEvent(reqKey);
      }
    } catch (err) {
      console.error('Moengage track_event error:', err);
    }
  }
}

async function trackEMSEvent(attributes, reqKey?: string) {
  const events = getStoredEvents('ems');
  const current = events[reqKey];

  if (current?.retries >= EMS_MAX_RETRIES) {
    delete events[reqKey];
    setStoredEvents(events, 'ems');
    return;
  }

  const sessionTracker: any = localStorage?.getItem(LocalStorage.SessionTracker)
    ? JSON.parse(localStorage.getItem(LocalStorage.SessionTracker))
    : {};
  const { event: eventName, ...eventData } = attributes;

  if (!isParkPlusWebView() && !getDeviceId()) {
    await generateBrowserFingerprint();
  }

  const body = {
    data: [
      {
        event_name: eventName,
        event_attributes: eventData,
        event_created_time: Date.now().toString(),
      },
    ],
  };
  const url = `${apiConfig('clickStream')}/api/v1/event`;
  const { error } = await postMethod({
    endPoint: url,
    body: body,
    headers: {
      ...getDefaultHeaders({ deviceId: true }),
      'session-id': sessionTracker.session_id,
      'session-start-time': sessionTracker.created_at?.toString(),
      'anonymous-id': localStorage.getItem(LocalStorage.SegmentAnonymousId),
    },
  });

  if (error) {
    const fresh = getStoredEvents('ems');
    if (!fresh[reqKey]) return;

    fresh[reqKey].retries += 1;
    setStoredEvents(fresh, 'ems');
    trackEMSEvent(attributes, reqKey);
  } else {
    const fresh = getStoredEvents('ems');
    if (fresh[reqKey]) cleanupQueuedEvent(reqKey, 'ems');
  }
}

async function trackClickHouseEvent(attributes) {
  //@ts-ignore
  let url = process.env.CLICK_HOUSE_URL;
  let setObj = { ...attributes };
  if (isDataValid(url)) {
    if (setObj?.table_name === 'dealer') {
      const url = `${apiConfig('seoService')}/api/v1/clickhouse-validator`;
      const { response, error } = await postMethod({
        endPoint: url,
        body: { ...setObj },
      });
      if (error) {
        console.log(`Failed to push event ${setObj.event}`, error);
      }
    } else {
      const img = new Image();
      url += '?';
      // eslint-disable-next-line no-restricted-syntax
      for (const [key, value] of Object.entries(setObj)) {
        url += `${key}=${value}&`;
      }
      img.src = url;
    }
  }
}

export function flushMoengageEvents() {
  const events = getStoredEvents();
  Object.entries(events).forEach(([reqKey, event]) => {
    trackMoengageEvent(event, reqKey);
  });
}

const flushQueuedEvents = () => {
  const queued = getStoredEvents('ems');
  if (!queued) return;

  Object.entries(queued).forEach(([key, obj]: any) => {
    const eventData = obj.payload;

    const { pathname, fullPathname } = getInitialRoute();
    const additionalData = {
      user_session_id: sessionStorage.getItem(SessionStorage.TrackingSessionId),
      initial_path: pathname,
      initial_full_path: fullPathname,
      primary_source: getPrimarySource(),
      referer_source: getRefererSource(),
      project_name: localStorage.getItem(LocalStorage.Theme),
      user_anonymous_id: localStorage.getItem(LocalStorage.SegmentAnonymousId),
    };
    trackEMSEvent({ ...eventData, ...additionalData }, key);
  });
};

let hasFlushed = false;
export const observeSessionKey = () => {
  if (!hasFlushed) {
    flushQueuedEvents();
    hasFlushed = true;
  }
};

export function trackEventFunction(
  name,
  dataObj,
  target = [EventTargets.Moengage, EventTargets.Clickhouse, EventTargets.Ems],
) {
  //@ts-ignore
  if (process.env.APP_ENV === Environments.Production) {
    const defaultAttr = getDefaultAttributes();
    const filteredObj = removeBadKeysFromObject({ ...defaultAttr, ...dataObj });
    let attr = isObject(filteredObj) ? JSON.parse(JSON.stringify(filteredObj)) : {};
    attr = normalizeTrackingAttributeAliases(attr);
    attr.event = name;

    if (target.includes(EventTargets.GTM)) {
      // @ts-ignore
      if (window?.dataLayer) {
        // @ts-ignore
        window?.dataLayer.push({
          event: name,
          ...attr,
        });
      }
    }

    if (isParkPlusWebView()) {
      const obj = {
        target: target.filter(item => item !== EventTargets.GTM),
        eventData: {
          event_name: name,
          ...attr,
        },
      };
      passMessageToNative(TRACK_EVENT, null, JSON.stringify(obj));
    } else {
      if (target.includes(EventTargets.Clickhouse)) trackClickHouseEvent(attr);
      if (target.includes(EventTargets.Ems)) {
        generateShaReqId(attr).then(reqKey => {
          addEventsToSessionStorage(attr, reqKey, 'ems');
          if (sessionStorage.getItem(SessionStorage.TrackingSessionId)) {
            trackEMSEvent(attr, reqKey);
          }
        });
      }
      if (target.includes(EventTargets.Moengage)) trackMoengageEvent(attr);
    }
  }
}
